import {
  AutoAwesome as AutoAwesomeIcon,
  PostAdd as PostAddIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  LocationOn as LocationOnIcon,
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  Tag as TagIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import { useState, useRef, useEffect } from 'react';
import { userModuleURL } from 'src/services/urlService';
import HttpServices from 'src/services/httpService';
import { useToaster } from 'src/utils/toaster/toasterContext';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ContentFocusSelector from './content-focus-selector.component';
import PostUploadComponent from '../new-post-new/post-upload.component';
import ImageDraggable from 'src/components/image/image-draggable';
import { CommonHelper } from 'src/utils/commonHelper';
import { useTranslation } from 'react-i18next';

const focusOptions = [
  { label: 'Articles', value: 'articles', iconKey: 'article' },
  { label: 'Education', value: 'education', iconKey: 'education' },
  { label: 'Trending', value: 'trending', iconKey: 'trending' },
  { label: 'Shopping', value: 'shopping', iconKey: 'shopping' },
  { label: 'Promotions', value: 'promotions', iconKey: 'promotions' },
];

const SemiAutomaticTab = ({
  checkGeneratingStatus,
  isGenerating,
  imageModels = [],
  selectedImageModel,
  setSelectedImageModel,
  handleHelperTextClick,
  pendingApprovalPosts,
}) => {
  const { t: translate } = useTranslation();
  const [contentLanguages, setContentLanguages] = useState(['English']);
  const [languageInput, setLanguageInput] = useState('');
  const { showToast } = useToaster();
  const [semiTotalPosts, setSemiTotalPosts] = useState(5);
  const [locationBasedEnabled, setLocationBasedEnabled] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationInput, setLocationInput] = useState('');
  const contentOptions = [
    { label: 'Text', info: '(Approx 25 text tokens) / post' },
    { label: 'Image', info: '(Approx 1 image  + 25 Text tokens) / post' },
    { label: 'Carousel', info: '(Approx 3-4 image  + 25 Text tokens) / post' },
    { label: 'Link', info: '(Upcoming)', disabled: true },
    { label: 'Short Videos', info: '(Upcoming)', disabled: true },
  ];
  const [semiPostsPerWeek, setSemiPostsPerWeek] = useState(3);
  const [postingHours, setPostingHours] = useState([9, 17]);
  const [contentTypes, setContentTypes] = useState(['Text']);
  const [customPostingHoursEnabled, setCustomPostingHoursEnabled] = useState(false);
  const [aiDeterminedHours, setAiDeterminedHours] = useState(true);

  const [includeTrending, setIncludeTrending] = useState(false);
  const [includeHashtags, setIncludeHashtags] = useState(true);
  const [prompt, setPrompt] = useState([]);
  const [promptInput, setPromptInput] = useState('');
  const [useBrandDetails, setUseBrandDetails] = useState(true);
  const [loading, setLoading] = useState(false);
  const loadingMessages = ['Generating Post...'];
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);
  const loadingIntervalRef = useRef(null);

  const today = new Date();
  const defaultTo = new Date();
  defaultTo.setDate(today.getDate() + 7);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(defaultTo);
  const [websiteUrls, setWebsiteUrls] = useState([]);
  const [websiteInput, setWebsiteInput] = useState('');
  const [contentFocus, setContentFocus] = useState([{ label: 'Education', value: 'education', iconKey: 'education' }]);

  const [isUploadMediaPopupOpen, setUploadMediaPopup] = useState(false);
  const [fileUploadprogress, setFileUploadprogress] = useState();
  const [mediaFiles, setMediaFiles] = useState([]);
  const [showUploadImage, setShowUploadImage] = useState(false);

  useEffect(() => {
    if (loading) {
      setLoadingMsgIdx(0);
      loadingIntervalRef.current = setInterval(() => {
        setLoadingMsgIdx((prev) => (prev + 1) % loadingMessages.length);
      }, 1800);
    } else {
      clearInterval(loadingIntervalRef.current);
      setLoadingMsgIdx(0);
    }
    return () => clearInterval(loadingIntervalRef.current);
  }, [loading]);

  const successCallback = (data, message) => {
    message && showToast(message, 's');
    checkGeneratingStatus();
  };
  const failureCallback = (message) => {
    message && showToast(message, 'e');
  };

  const handleStartAuto = () => {
    // If user wants to upload their own images, but no media is present, show error and do not proceed
    if (showUploadImage && mediaFiles.length === 0) {
      showToast('Please upload at least one media file before generating posts.', 'e');
      return;
    }
    console.log(mediaFiles, 'mediaFiles in SemiAutomaticTab');
    console.log(showUploadImage, 'showUploadImage in SemiAutomaticTab');
    let hoursDescription;
    if (aiDeterminedHours) {
      hoursDescription = 'Let AI determine optimal posting times';
    } else if (customPostingHoursEnabled) {
      hoursDescription = `Between ${postingHours[0]}:00 and ${postingHours[1]}:00`;
    } else {
      hoursDescription = 'No specific time preference';
    }

    // Add promptInput if not empty and not already in prompt
     let finalPrompt = [...prompt];
    if (promptInput.trim()) {
      const newPrompt = promptInput.trim();
      if (!finalPrompt.includes(newPrompt)) {
        finalPrompt = [...finalPrompt, newPrompt];
      }
      setPrompt(finalPrompt);
      setPromptInput('');
    }

    startGeneratingPostsWithPrompt(finalPrompt);
  };

  // Helper to pass prompt to API
  const startGeneratingPostsWithPrompt = (finalPrompt) => {
    if (!semiTotalPosts || semiTotalPosts === '' || isNaN(semiTotalPosts)) {
      showToast('Please enter the number of posts per week.', 'e');
      return;
    }
    setLoading(true);
    // Ensure final locations are processed before API call
    let finalLocations = locations;
    if (locationInput.trim()) {
      const newLocations = locationInput
        .split(',')
        .map((loc) => loc.trim())
        .filter((loc) => loc && !locations.includes(loc));
      finalLocations = [...locations, ...newLocations];
      setLocations(finalLocations); // Update state for UI consistency
      setLocationInput('');
    }
    
    // Ensure final website URLs are processed before API call
    let finalWebsiteUrls = websiteUrls;
    if (websiteInput.trim()) {
      const newUrls = websiteInput
        .split(',')
        .map((url) => url.trim())
        .filter((url) => url && isValidHttpsUrl(url) && !websiteUrls.includes(url));
      finalWebsiteUrls = [...websiteUrls, ...newUrls];
      setWebsiteUrls(finalWebsiteUrls); // Update state for UI consistency
      setWebsiteInput('');
    }

    const payload = {
      total_posts: semiTotalPosts,
      date_from: fromDate,
      date_to: toDate,
      posts_per_week: semiPostsPerWeek,
      posting_hours: postingHours,
      ai_determined_hours: aiDeterminedHours,
      custom_posting_hours_enabled: customPostingHoursEnabled,
      content_types: contentTypes,
      content_languages: contentLanguages,
      include_trending: includeTrending,
      include_hashtags: includeHashtags,
      prompt: finalPrompt,
      use_brand_details: useBrandDetails,
      location_based_enabled: locationBasedEnabled,
      locations: finalLocations, // Use final locations here
      website_urls: finalWebsiteUrls, // Use final website URLs here
      type_of_contents: contentFocus.map((focus) => focus.value),
      model: selectedImageModel ? selectedImageModel.model : undefined,
      model_provider: selectedImageModel ? selectedImageModel.model_provider : undefined,
    };

    HttpServices.Post({
      url: userModuleURL.generateSemiAutoPost,
      body: payload,
      successCallback: (data, message) => {
        setLoading(false);
        successCallback(data, message);
      },
      failureCallback: (message) => {
        setLoading(false);
        failureCallback(message);
      },
    });
  };

  const handleContentTypeChange = (event) => {
    const {
      target: { value },
    } = event;
    setContentTypes(typeof value === 'string' ? value.split(',') : value);
  };

  const handleCustomHoursToggle = () => {
    setCustomPostingHoursEnabled(!customPostingHoursEnabled);
    if (!customPostingHoursEnabled) {
      setAiDeterminedHours(false);
    }
  };

  const handleAiHoursToggle = () => {
    setAiDeterminedHours(!aiDeterminedHours);
    if (!aiDeterminedHours) {
      setCustomPostingHoursEnabled(false);
    }
  };

  const handleLocationToggle = () => {
    setLocationBasedEnabled(!locationBasedEnabled);
  };

  const handleLocationAdd = (event) => {
    if (event.key === 'Enter' && locationInput.trim()) {
      setLocations([...locations, locationInput.trim()]);
      setLocationInput('');
    }
  };

  const handleFinalLocation = () => {
    if (locationInput.trim()) {
      const newLocations = locationInput
        .split(',')
        .map((loc) => loc.trim())
        .filter((loc) => loc && !locations.includes(loc));
      setLocations([...locations, ...newLocations]);
      setLocationInput(''); // Clear the input field
    }
  };

  const handleLocationDelete = (locationToDelete) => () => {
    setLocations(locations.filter((location) => location !== locationToDelete));
  };

  const handleLanguageAdd = (event) => {
    if (event.key === 'Enter' && languageInput.trim()) {
      setContentLanguages([...contentLanguages, languageInput.trim()]);
      setLanguageInput('');
    }
  };

  const handleLanguageDelete = (languageToDelete) => () => {
    setContentLanguages(contentLanguages.filter((lang) => lang !== languageToDelete));
  };

  const handlePromptAdd = (event) => {
    if (event.key === 'Enter' && promptInput.trim()) {
      const newPrompt = promptInput.trim();
      if (!prompt.includes(newPrompt)) {
        setPrompt([...prompt, newPrompt]);
      }
      setPromptInput('');
    }
  };

  const handlePromptDelete = (promptToDelete) => () => {
    setPrompt(prompt.filter((k) => k !== promptToDelete));
  };

  const handleUploadMediaPopup = (show = false) => {
    setUploadMediaPopup(show);
  };

  // Add handleUploadMediaToServer, similar to PostContentComponent
  const handleUploadMediaToServer = (files, privateFiles) => {
    if ((!files || files.length === 0) && privateFiles && privateFiles.length > 0) {
      const uploadFilesProps = {
        privateFiles,
        successCallback: (data, message) => privateFileUploadSuccessCallback(data, message),
        failureCallback,
      };
      CommonHelper.UploadPrivateMediaFilesToServer({ ...uploadFilesProps });
    } else {
      let uploadFilesProps = {
        files,
        setFileUploadprogress,
        successCallback: (data, message) => {
          fileUploadSuccessCallback(data, message, privateFiles);
        },
        failureCallback,
      };
      CommonHelper.UploadMediaFilesToServer({ ...uploadFilesProps });
    }
  };

  // Success/failure callbacks for upload
  const privateFileUploadSuccessCallback = (data, message, uploadFiles = []) => {
    const mFiles = [...data?.media_datas, ...uploadFiles];
    message && showToast(message, 's');
    setMediaFiles((prev) => [...prev, ...mFiles]);
    setUploadMediaPopup(false);
  };

  const fileUploadSuccessCallback = (data, message, privateFiles) => {
    if (privateFiles && privateFiles.length > 0) {
      const uploadFilesProps = {
        privateFiles,
        successCallback: (privateData, msg) => privateFileUploadSuccessCallback(privateData, msg, data),
        failureCallback,
      };
      CommonHelper.UploadPrivateMediaFilesToServer({ ...uploadFilesProps });
    } else {
      message && showToast(message, 's');
      setMediaFiles((prev) => [...prev, ...data]);
      setUploadMediaPopup(false);
    }
  };

  const handleUploadMedia = (files, privateFiles = []) => {
    handleUploadMediaToServer(files, privateFiles);
  };

  const handleReOrderFiles = (oldIndex, newIndex) => {
    setMediaFiles((prevFiles) => {
      const files = [...prevFiles];
      const [removed] = files.splice(oldIndex, 1);
      files.splice(newIndex, 0, removed);
      return files;
    });
  };

  const handleRemoveFiles = (mediaUrlOrIdx) => {
    console.log('Removing media file:', mediaUrlOrIdx);
    setMediaFiles((prevFiles) => {
      // Try to remove by index or by media_url/name
      if (typeof mediaUrlOrIdx === 'number') {
        return prevFiles.filter((_, idx) => idx !== mediaUrlOrIdx);
      }
      return prevFiles.filter((file) => file.media_url !== mediaUrlOrIdx && file.name !== mediaUrlOrIdx);
    });
  };

  // Helper to validate URL
  const isValidHttpsUrl = (url) => {
    try {
      const u = new URL(url);
      return u.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Add contentDisabled logic if needed
  const contentDisabled = false; // You can update this logic as needed

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 1, md: 3 } }}>
      {/* --- START GENERATING BUTTON OUTSIDE, AT TOP --- */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleStartAuto}
          sx={{
            py: 1.5,
            px: 4,
            fontSize: '1.1rem',
            borderRadius: 2,
            minWidth: 250,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4,
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.2s ease',
          }}
          startIcon={<AutoAwesomeIcon />}
          disabled={isGenerating || loading}
        >
          {loading || isGenerating
            ? loadingMessages[loadingMsgIdx]
            : translate('auto_post.start_generating_post', 'Start Generating Post')}
        </Button>
        {pendingApprovalPosts?.length > 0 && (
          <Typography variant="caption" sx={{ fontSize: '0.70rem', display: 'flex', alignItems: 'center' }}>
            {translate('auto_post.want_to_view_generate_posts', 'Want to view the generated posts?')}{' '}
            <Typography
              variant="subtitle2"
              component="span"
              sx={{
                cursor: 'pointer',
                color: 'primary.main',
                textDecoration: 'underline',
                ml: 0.5,
                '&:hover': { textDecoration: 'none' },
              }}
              onClick={handleHelperTextClick}
            >
              {translate('post_footer.click_here', 'Click here')}
            </Typography>
          </Typography>
        )}
      </Box>
      {/* --- PROMPT, WEBSITE, NO OF POSTS, CONTENT TYPE CARD AT TOP --- */}
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, mb: 2.5 }}>
          <AutoAwesomeIcon color="primary" sx={{ mr: 1.5, fontSize: 28 }} />
          {translate('auto_post.generate_content', 'Generate Content')}
        </Typography>
        <Grid container spacing={3}>
          {/* First row: Number of Posts & Content Types */}
          {/* --- MEDIA UPLOAD SECTION --- */}
          {showUploadImage && (
            <Grid item xs={12} md={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showUploadImage}
                    onChange={(_, checked) => setShowUploadImage(checked)}
                    color="primary"
                  />
                }
                label={translate('common.upload_your_own_images', 'Upload your own images')}
                sx={{ mb: 2 }}
              />
              {showUploadImage && (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleUploadMediaPopup(true)}
                    sx={{ mb: 2 }}
                  >
                    Upload Media Files
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                    To support max social, upload 1x1 (square) images.
                  </Typography>
                  {mediaFiles.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <ImageDraggable
                        mediaFiles={mediaFiles}
                        handleRemoveFiles={handleRemoveFiles}
                        handleReOrderFiles={handleReOrderFiles}
                      />
                    </Box>
                  )}
                </>
              )}
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={translate('auto_post.number_of_posts', 'Number of Posts')}
              type="number"
              value={semiTotalPosts}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, ''); // Remove non-digits
                const num = value === '' ? '' : parseInt(value, 10);
                if (value === '' || (num >= 1 && num <= 50)) {
                  setSemiTotalPosts(num);
                }
              }}
              inputProps={{ min: 1, max: 50 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip
                      title={translate('auto_post.number_of_posts_tooltip', 'Total posts to generate in this schedule')}
                    >
                      <InfoIcon color="action" fontSize="small" />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <FormHelperText>{translate('auto_post.posts_per_week_helper', 'Between 1 and 50 posts')}</FormHelperText>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="content-types-label-top">
                {translate('auto_post.content_types', 'Content Types')}
              </InputLabel>
              <Select
                labelId="content-types-label-top"
                multiple
                value={contentTypes}
                onChange={handleContentTypeChange}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                )}
                label={translate('auto_post.content_types', 'Content Types')}
              >
                {contentOptions.map((option) => (
                  <MenuItem key={option.label} value={option.label} disabled={contentDisabled || option.disabled}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <span>{option.label}</span>
                      <Typography variant="caption" color="text.secondary">
                        {option.info}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{translate('auto_post.select_content_types', 'Select content types')}</FormHelperText>
            </FormControl>
          </Grid>
          {/* Second row: Prompt & Website URLs */}
      <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={translate('auto_post.prompt', 'Prompt')}
              value={promptInput}
              onChange={(e) => setPromptInput(e.target.value)}
              onKeyDown={handlePromptAdd}
              placeholder={'Type a prompt and press Enter to add'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={translate('auto_post.prompt_tooltip', 'Press Enter to add each prompt. Commas are allowed inside prompts.')}>
                      <InfoIcon color="action" fontSize="small" />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {prompt.map((p, idx) => (
                <Chip key={idx} label={p} onDelete={handlePromptDelete(p)} deleteIcon={<CloseIcon />} />
              ))}
            </Box>
            <FormHelperText>
              {translate('auto_post.prompt_helper', 'Press Enter to add each prompt. You can include commas inside a prompt.')}
            </FormHelperText>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={translate('auto_post.website_urls', 'Website URLs')}
              value={websiteInput}
              onChange={(e) => setWebsiteInput(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && websiteInput.trim()) {
                  const newUrls = websiteInput
                    .split(',')
                    .map((k) => k.trim())
                    .filter((k) => k && isValidHttpsUrl(k) && !websiteUrls.includes(k));
                  setWebsiteUrls([...websiteUrls, ...newUrls]);
                  setWebsiteInput('');
                }
              }}
              placeholder={translate(
                'auto_post.website_urls_placeholder',
                'Press Enter to add valid https URL, or comma to add multiple'
              )}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip
                      title={translate(
                        'auto_post.website_urls_tooltip',
                        'Add website URLs for reference or content inspiration (must be https)'
                      )}
                    >
                      <InfoIcon color="action" fontSize="small" />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              error={
                Boolean(websiteInput) && websiteInput.split(',').some((k) => k.trim() && !isValidHttpsUrl(k.trim()))
              }
              helperText={
                Boolean(websiteInput) && websiteInput.split(',').some((k) => k.trim() && !isValidHttpsUrl(k.trim()))
                  ? translate('auto_post.website_urls_error', 'All URLs must be valid https URLs.')
                  : translate(
                      'auto_post.website_urls_helper',
                      'Press Enter to add. Added URLs will appear as chips above.'
                    )
              }
              sx={{ mb: 1, fontWeight: 500, fontSize: '1.1rem' }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {websiteUrls.map((url) => (
                <Chip
                  key={url}
                  label={url}
                  onDelete={() => setWebsiteUrls(websiteUrls.filter((u) => u !== url))}
                  deleteIcon={<CloseIcon />}
                />
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControl fullWidth disabled={contentDisabled || imageModels.length === 0} sx={{ mb: 2 }}>
              <InputLabel id="image-model-label">{translate('auto_post.image_model', 'Image Model')}</InputLabel>
              <Select
                labelId="image-model-label"
                value={selectedImageModel ? selectedImageModel.model : ''}
                label={translate('auto_post.image_model', 'Image Model')}
                onChange={(e) => {
                  const model = imageModels.find((m) => m.model === e.target.value);
                  setSelectedImageModel(model);
                }}
              >
                {imageModels.map((modelInfo) => (
                  <MenuItem key={modelInfo.model + modelInfo.model_provider} value={modelInfo.model}>
                    {modelInfo.display_text}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {translate('auto_post.select_image_model', 'Select the image generation model')}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={12}>
            <ContentFocusSelector value={contentFocus} onChange={setContentFocus} focusOptions={focusOptions} />
          </Grid>
        </Grid>
      </Paper>
      {/* --- SCHEDULE SECTION --- */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, mb: 2.5 }}>
          <ScheduleIcon color="primary" sx={{ mr: 1.5, fontSize: 28 }} />
          {translate('auto_post.schedule_and_duration', 'Schedule & Duration')}
        </Typography>
        <Grid container spacing={3}>
          {/* Removed Number of Posts field from Schedule & Duration */}
          <Grid item xs={12} md={8}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <DatePicker
                    label={translate('auto_post.from', 'From')}
                    value={fromDate}
                    minDate={today}
                    onChange={(newValue) => {
                      setFromDate(newValue);
                      if (toDate < newValue) setToDate(newValue);
                    }}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label={translate('auto_post.to', 'To')}
                    value={toDate}
                    minDate={fromDate}
                    onChange={(newValue) => setToDate(newValue)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>
            <Box sx={{ mt: 1 }}>
              <FormHelperText>
                {translate('auto_post.selected_range', 'Selected range: {from} - {to}', {
                  from: fromDate ? fromDate.toLocaleDateString() : '',
                  to: toDate ? toDate.toLocaleDateString() : '',
                })}
              </FormHelperText>
            </Box>
          </Grid>
        </Grid>
        <Box sx={{ mt: 3 }}>
          <FormControlLabel
            control={<Switch checked={aiDeterminedHours} onChange={handleAiHoursToggle} color="primary" />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {translate(
                  'auto_post.let_ai_determine_optimal_posting_times',
                  'Let AI determine optimal posting times'
                )}
                <Tooltip
                  title={translate(
                    'auto_post.ai_will_analyze_audience_and_choose_best_times',
                    'Our AI will analyze your audience and choose the best times to post'
                  )}
                >
                  <InfoIcon color="action" fontSize="small" sx={{ ml: 1 }} />
                </Tooltip>
              </Box>
            }
            sx={{ mb: 1 }}
          />
          {!aiDeterminedHours && (
            <Box>
              <Typography gutterBottom sx={{ mb: 2 }}>
                {translate('auto_post.preferred_posting_hours', 'Preferred Posting Hours')}
                <Tooltip
                  title={translate(
                    'auto_post.we_will_aim_to_post_during_these_hours_for_maximum_engagement',
                    "We'll aim to post during these hours for maximum engagement"
                  )}
                >
                  <InfoIcon color="action" fontSize="small" sx={{ ml: 1 }} />
                </Tooltip>
              </Typography>
              <Slider
                value={postingHours}
                onChange={(_, newValue) => setPostingHours(newValue)}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value}:00`}
                min={0}
                max={24}
                step={1}
                marks={[
                  { value: 0, label: '12AM' },
                  { value: 6, label: '6AM' },
                  { value: 12, label: '12PM' },
                  { value: 18, label: '6PM' },
                  { value: 24, label: '12AM' },
                ]}
                sx={{ mt: 3, width: '95%', mb: 3, mx: 2 }}
              />
              <FormHelperText>
                {translate('auto_post.current_range', 'Current range: {start}:00 - {end}:00', {
                  start: postingHours[0],
                  end: postingHours[1],
                })}
              </FormHelperText>
            </Box>
          )}
        </Box>
      </Paper>

      {/* --- CONTENT PREFERENCES SECTION --- */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, mb: 2.5 }}>
          <PostAddIcon color="primary" sx={{ mr: 1.5, fontSize: 28 }} />
          {translate('auto_post.content_preferences', 'Content Preferences')}
        </Typography>
        <Grid container spacing={3}>
          {/* Content Languages now at the top, full width on md and up */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={translate('auto_post.content_languages', 'Content Languages')}
              value={languageInput}
              onChange={(e) => setLanguageInput(e.target.value)}
              onKeyDown={handleLanguageAdd}
              placeholder={translate('auto_post.press_enter_to_add_language', 'Press Enter to add language')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip
                      title={translate(
                        'auto_post.specify_languages_for_generated_content',
                        'Specify languages for generated content'
                      )}
                    >
                      <InfoIcon color="action" fontSize="small" />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {contentLanguages.map((language) => (
                <Chip
                  key={language}
                  label={language}
                  onDelete={handleLanguageDelete(language)}
                  deleteIcon={<CloseIcon />}
                />
              ))}
            </Box>
            <FormHelperText sx={{ mb: 2 }}>
              {translate(
                'auto_post.added_languages_will_appear_as_chips_above',
                'Added languages will appear as chips above'
              )}
            </FormHelperText>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
              {translate('auto_post.content_options', 'Content Options')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeTrending}
                    onChange={() => setIncludeTrending(!includeTrending)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUpIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    {translate('auto_post.trending_topics', 'Trending Topics')}
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={includeHashtags}
                    onChange={() => setIncludeHashtags(!includeHashtags)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TagIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    {translate('auto_post.hashtags', 'Hashtags')}
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={useBrandDetails}
                    onChange={() => setUseBrandDetails(!useBrandDetails)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SettingsIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    {translate('auto_post.brand_details', 'Brand Details')}
                  </Box>
                }
              />
              <FormControlLabel
                control={<Switch checked={false} disabled color="primary" />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {translate(
                      'auto_post.ai_engagement_comment_and_message_upcoming',
                      'AI Engagement Comment & Message (Upcoming)'
                    )}
                    <Tooltip
                      title={translate(
                        'auto_post.coming_soon_ai_powered_comment_message_replies',
                        'Coming soon: AI-powered comment/message replies'
                      )}
                    >
                      <InfoIcon color="action" fontSize="small" sx={{ ml: 1 }} />
                    </Tooltip>
                  </Box>
                }
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* --- LOCATION SECTION --- */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, mb: 2.5 }}>
          <LocationOnIcon color="primary" sx={{ mr: 1.5, fontSize: 28 }} />
          {translate('auto_post.location_based_content', 'Location-based Content')}
        </Typography>
        <FormControlLabel
          control={<Switch checked={locationBasedEnabled} onChange={handleLocationToggle} color="primary" />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {translate('auto_post.enable_location_based_content', 'Enable location-based content')}
              <Tooltip
                title={translate(
                  'auto_post.enable_to_generate_content_specific_to_certain_locations',
                  'Enable to generate content specific to certain locations'
                )}
              >
                <InfoIcon color="action" fontSize="small" sx={{ ml: 1 }} />
              </Tooltip>
            </Box>
          }
          sx={{ mb: 1 }}
        />
        {locationBasedEnabled && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label={translate('auto_post.add_locations', 'Add Locations')}
              value={locationInput}
              onChange={(e) => setLocationInput(e.target.value)}
              onKeyDown={handleLocationAdd}
              placeholder={translate('auto_post.press_enter_to_add_location', 'Press Enter to add location')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip
                      title={translate(
                        'auto_post.add_locations_for_localized_content',
                        'Add locations for localized content'
                      )}
                    >
                      <InfoIcon color="action" fontSize="small" />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
              {locations.map((location) => (
                <Chip
                  key={location}
                  label={location}
                  onDelete={handleLocationDelete(location)}
                  deleteIcon={<CloseIcon />}
                />
              ))}
            </Box>
            <FormHelperText>
              {translate(
                'auto_post.added_locations_will_appear_as_chips_above',
                'Added locations will appear as chips above'
              )}
            </FormHelperText>
          </Box>
        )}
      </Paper>

      {isUploadMediaPopupOpen && (
        <PostUploadComponent
          handleClose={handleUploadMediaPopup}
          fileUploadprogress={fileUploadprogress}
          handleUpload={handleUploadMedia}
        />
      )}
    </Box>
  );
};

export default SemiAutomaticTab;
